import { useOutletContext } from 'react-router';
import FileExplorer from '@/components/FileExplorer/FileExplorer';
import { styled } from '@mui/material/styles';
import ResourcesPageHeader from '@/components/ResourcesPageHeader/ResourcesPageHeader';
import {
  fetchComplianceDocuments,
  fetchComplianceDocumentsByFolder,
  fetchComplianceDocumentsFolderStructure,
  fetchFilters,
  fetchUnifiedSearch,
} from '@/lib/api';
import { useDownload } from '@/hooks/useDownload';
import useInfiniteScroll from '@/hooks/useInfiniteScroll';
import {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import {
  SortByOptions,
  mapFiltersToParams,
  parseSearchHitPath,
} from './ResourcePageHelpers';
import { FilterSchema } from '@/components/ResourcesSidebar/ResourcesSidebarHelpers';
import {
  ComplianceDocument,
  FolderStructureResponse,
  FolderStructureItem,
  WithFormatted,
  UnifiedSearchResponse,
} from '@/lib/types';
import { useFileExplorerData } from '@/hooks/useFileExplorerData';
import { assetQueryKeys } from '@/lib/assetQueryKeys';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import ResourcesSearchPropositions from '@/components/ResourcesPageHeader/ResourcesSearchPropositions';
import { useScrollContainer } from '@/contexts/ScrollContainerContext';
import Button from '@/components/Button/Button';
import DownloadIcon from '@/assets/download.svg?react';
import { Mode } from '@/components/FileExplorer/FileExplorerHelpers';
import { useBasketContext } from '@/contexts/BasketContext';
import BasketBar from '@/components/ShareBasketModal/BasketBar';
import ShareBasketModal from '@/components/ShareBasketModal/ShareBasketModal';
import ModeSwitch from '@/components/FileExplorer/ModeSwitch';
import { ga } from '@/lib/analytics';

const COMPLIANCE_HIDDEN_ROOT = '';
const EMPTY_FOLDER_STRUCTURE: FolderStructureResponse = {
  folders: [
    {
      path: COMPLIANCE_HIDDEN_ROOT,
      name: '',
      file_count: 0,
      total_file_count: 0,
      subfolders: [],
    },
  ],
  total_files: 0,
};

const ComplianceDocumentsResourcesPage = () => {
  const intl = useIntl();
  const { data } = useQuery({
    queryKey: ['filters'],
    queryFn: fetchFilters,
  });
  const [searchText, setSearchText] = useState('');
  const [committedSearchText, setCommittedSearchText] = useState('');
  const [sortBy, setSortBy] = useState<string>(
    SortByOptions[0].value,
  );
  const { idsFor, addMany, removeMany } = useBasketContext();
  const [shareOpen, setShareOpen] = useState(false);
  const selected = useMemo(
    () => idsFor('compliance_documents'),
    [idsFor],
  );
  const [focusedDocumentId, setFocusedDocumentId] = useState<
    number | null
  >(null);
  const [path, setPath] = useState<string[]>([]);
  const [gridMode, setGridMode] = useState<
    'explorer' | 'search' | 'proposition'
  >('explorer');
  const [currentView, setCurrentView] = useState<Mode>('grid');
  const [propositionDoc, setPropositionDoc] =
    useState<WithFormatted<ComplianceDocument> | null>(null);

  const trimmedCommittedSearchText = committedSearchText.trim();
  const hasSufficientCommittedSearchText =
    trimmedCommittedSearchText.length >= 2;
  // const [currentView, setCurrentView] = useState<'list' | 'grid'>(
  //   'list',
  // );
  const filters: FilterSchema = useOutletContext();
  const filterParams = mapFiltersToParams(filters, sortBy);
  const scrollContainerRef = useScrollContainer();
  const searchPropositionsPopperRef = useRef<HTMLDivElement>(null);
  const selectedMarket = data?.markets?.find(
    (filter) => filter.market_id === filters?.market?.[0],
  );

  const trimmedSearchText = searchText.trim();
  const hasSufficientSearchText = trimmedSearchText.length >= 2;

  const {
    data: searchSuggestions,
    isLoading: isSearchSuggestionsLoading,
  } = useInfiniteScroll<
    WithFormatted<ComplianceDocument>,
    Record<string, unknown>,
    UnifiedSearchResponse
  >({
    section: 'Compliance Resources Search Propositions',
    fetchFn: ({ offset, limit }) =>
      fetchUnifiedSearch({
        q: trimmedSearchText,
        ttype: ['compliance_documents'],
        ...filterParams,
        limit,
        offset,
      }),
    queryKey: [
      ...assetQueryKeys.compliance_documents
        .unifiedSearchPropositions,
      trimmedSearchText,
      ...Object.values(filterParams),
    ],
    scrollContainerRef: searchPropositionsPopperRef,
    useWindowScroll: false,
    pageSize: 5,
    scrollThreshold: 80,
    enabled: hasSufficientSearchText,
    extractData: (response) =>
      (response.compliance_documents
        ?.hits as WithFormatted<ComplianceDocument>[]) ?? [],
  });

  const {
    folderStructure,
    filesFlatData,
    loading,
    loadingNextPage,
    totalCount,
  } = useFileExplorerData({
    filters,
    sortBy,
    path,
    enabled: gridMode === 'explorer',
    scrollContainerRef: scrollContainerRef || undefined,
    folderStructureFn: fetchComplianceDocumentsFolderStructure,
    folderStructureQueryKey:
      assetQueryKeys.compliance_documents.folderStructure,
    fetchFilesByFolderFn: fetchComplianceDocumentsByFolder,
    fetchFilesByFolderQueryKey:
      assetQueryKeys.compliance_documents.byFolder,
    fetchFilesFlatFn: fetchComplianceDocuments,
    fetchFilesFlatQueryKey: assetQueryKeys.compliance_documents.flat,
  });

  const { download, loading: downloadLoading } = useDownload();

  useEffect(() => {
    if (gridMode === 'proposition') return;
    if (
      path.length >= 3 &&
      selectedMarket &&
      path?.[2] !== selectedMarket.market_name
    ) {
      setPath([path?.[0], path?.[1], selectedMarket.market_name]);
    }
  }, [gridMode, selectedMarket, path]);

  const hasSearchText = hasSufficientSearchText;

  const hasActiveSearchOrFilter =
    (searchText && hasSearchText) ||
    Object.values(filters).length > 0;

  const extractComplianceSearchHits = useCallback(
    (response: UnifiedSearchResponse) => {
      const hits =
        (response.compliance_documents
          ?.hits as WithFormatted<ComplianceDocument>[]) ?? [];
      return hits.map((doc) => {
        if (!doc.file_path) return doc;
        return {
          ...doc,
          file_path: parseSearchHitPath(
            doc.file_path,
            COMPLIANCE_HIDDEN_ROOT,
          ),
        };
      });
    },
    [],
  );

  const clearSearchState = useCallback(() => {
    setSearchText('');
    setCommittedSearchText('');
    setGridMode('explorer');
    setPropositionDoc(null);
    setFocusedDocumentId(null);
    setPath([]);
  }, [
    setCommittedSearchText,
    setFocusedDocumentId,
    setGridMode,
    setPath,
    setPropositionDoc,
    setSearchText,
  ]);

  const handlePathChange = useCallback((nextPath: string[]) => {
    setPath(nextPath);
    setGridMode('explorer');
    setCommittedSearchText('');
    setPropositionDoc(null);
    setFocusedDocumentId(null);
  }, []);

  const {
    data: searchResultsFlat,
    isLoading: isSearchResultsLoading,
    isFetchingNextPage: isSearchResultsFetchingNextPage,
    isFetching: isSearchResultsFetching,
    total_count: searchResultsTotalCount,
  } = useInfiniteScroll<
    WithFormatted<ComplianceDocument>,
    Record<string, unknown>,
    UnifiedSearchResponse
  >({
    section: 'Compliance Resources Search',
    fetchFn: ({ offset, limit }) =>
      fetchUnifiedSearch({
        q: trimmedCommittedSearchText,
        ttype: ['compliance_documents'],
        ...filterParams,
        limit,
        offset,
      }),
    queryKey: [
      ...assetQueryKeys.compliance_documents.resourcesSearch,
      trimmedCommittedSearchText,
      ...Object.values(filterParams),
    ],
    scrollContainerRef: scrollContainerRef || undefined,
    useWindowScroll: false,
    pageSize: 20,
    scrollThreshold: 200,
    enabled:
      gridMode === 'search' && hasSufficientCommittedSearchText,
    extractData: extractComplianceSearchHits,
  });

  // Reported once the results of an accepted search are in, so that
  // `result_count` can be sent along with the search term.
  const searchAcceptSentRef = useRef<string | null>(null);

  useEffect(() => {
    if (gridMode !== 'search' || !hasSufficientCommittedSearchText) {
      searchAcceptSentRef.current = null;
      return;
    }
    if (isSearchResultsLoading) return;
    if (searchAcceptSentRef.current === trimmedCommittedSearchText)
      return;

    searchAcceptSentRef.current = trimmedCommittedSearchText;
    ga.trackSearchAccept({
      search_term: trimmedCommittedSearchText,
      result_count:
        searchResultsTotalCount ?? searchResultsFlat.length,
    });
  }, [
    gridMode,
    hasSufficientCommittedSearchText,
    isSearchResultsLoading,
    searchResultsFlat.length,
    searchResultsTotalCount,
    trimmedCommittedSearchText,
  ]);

  const propositionFolderStructure = useMemo(() => {
    if (gridMode !== 'proposition') return undefined;
    if (path.length === 0) return undefined;

    const createFolderItem = (idx: number): FolderStructureItem => {
      const isLeaf = idx === path.length - 1;
      return {
        path: path.slice(0, idx + 1).join('/'),
        name: path[idx],
        file_count: 0,
        total_file_count: 0,
        subfolders: isLeaf ? [] : [createFolderItem(idx + 1)],
      };
    };

    return {
      folders: [
        {
          path: path[0],
          name: '',
          file_count: 0,
          total_file_count: 0,
          subfolders: path.length > 1 ? [createFolderItem(1)] : [],
        },
      ],
      total_files: 0,
    };
  }, [gridMode, path]);

  const explorerFiles: ComplianceDocument[] = useMemo(() => {
    if (gridMode === 'proposition') {
      return propositionDoc ? [propositionDoc] : [];
    }

    const baseFiles = (
      gridMode === 'search' ? searchResultsFlat : filesFlatData
    ) as ComplianceDocument[];

    if (focusedDocumentId == null) return baseFiles;
    return baseFiles.filter((doc) => doc.id === focusedDocumentId);
  }, [
    focusedDocumentId,
    filesFlatData,
    gridMode,
    propositionDoc,
    searchResultsFlat,
  ]);

  // FileExplorer hands back the full next selection for this category;
  // reconcile it against the basket rather than replacing the basket, which
  // also holds the other category's picks.
  const handleSelect = useCallback(
    (next: number[]) => {
      const nextSet = new Set(next);
      if (next.length > 0) {
        ga.trackAssetMultiSelect({ assets_count: next.length });
      }
      addMany('compliance_documents', next);
      removeMany(
        'compliance_documents',
        idsFor('compliance_documents').filter(
          (id) => !nextSet.has(id),
        ),
      );
    },
    [addMany, removeMany, idsFor],
  );

  const loadedIds = useMemo(
    () => explorerFiles.map((file) => file.id),
    [explorerFiles],
  );
  const loadedTotal =
    gridMode === 'search' ? searchResultsTotalCount : totalCount;

  // The basket spans sections; this page's Download must only ever act on
  // rows actually on screen, so scope it to what is loaded here.
  const viewSelected = useMemo(
    () => loadedIds.filter((id) => selected.includes(id)),
    [loadedIds, selected],
  );

  const handleDownloadClick = useMemo(() => {
    return () => {
      if (!viewSelected.length) return;

      const downloadParams: {
        category: 'compliance_documents';
        file_ids: number[];
        extension?: string;
        entity_name?: string;
      } = {
        category: 'compliance_documents',
        file_ids: viewSelected,
      };

      if (viewSelected.length === 1) {
        const allFilesForNaming = (
          gridMode === 'proposition'
            ? propositionDoc
              ? ([propositionDoc] as ComplianceDocument[])
              : []
            : gridMode === 'search'
              ? ((searchResultsFlat as ComplianceDocument[]) ?? [])
              : ((filesFlatData as ComplianceDocument[]) ?? [])
        ) as ComplianceDocument[];

        const selectedFile = allFilesForNaming.find(
          (file) => file.id === viewSelected[0],
        );
        if (selectedFile?.extension) {
          downloadParams.extension = selectedFile.extension;
        }
        if (selectedFile?.name) {
          downloadParams.entity_name = selectedFile.name;
        }
      }

      ga.trackAssetBulkDownload({
        assets_count: viewSelected.length,
      });
      download(downloadParams);
    };
  }, [
    gridMode,
    download,
    filesFlatData,
    propositionDoc,
    searchResultsFlat,
    viewSelected,
  ]);
  return (
    <Root className="wide-scrollbar">
      <ResourcesPageHeader
        title={
          <FormattedMessage
            id="searchWithinComplianceDocuments"
            defaultMessage="Search within Compliance Documents"
          />
        }
        searchPlaceholder={intl.formatMessage({
          id: 'searchByNameCompliance',
          defaultMessage: 'Search by name',
        })}
        sortBy={sortBy}
        searchText={searchText}
        onSortByChange={setSortBy}
        onSearchTextChange={setSearchText}
        options={SortByOptions}
        loading={isSearchSuggestionsLoading}
        searchPropositionsPopperRef={searchPropositionsPopperRef}
        onSearchAccept={() => {
          if (trimmedSearchText.length >= 2) {
            setCommittedSearchText(trimmedSearchText);
            setGridMode('search');
            setPropositionDoc(null);
            setFocusedDocumentId(null);
            setPath([]);
          } else {
            setCommittedSearchText('');
            setGridMode('explorer');
            setPropositionDoc(null);
            setFocusedDocumentId(null);
          }
        }}
        onSearchCancel={clearSearchState}
        actions={
          <>
            <Button
              variant="contained"
              onClick={handleDownloadClick}
              disabled={!viewSelected.length}
              aria-label="Download selected"
              loading={downloadLoading}
            >
              <DownloadIcon />
              <FormattedMessage
                id="downloadWithCount"
                defaultMessage="Download{suffix}"
                values={{
                  suffix: viewSelected.length
                    ? ` (${viewSelected.length})`
                    : '',
                }}
              />
            </Button>
            <ModeSwitch
              mode={currentView}
              onChange={setCurrentView}
            />
          </>
        }
        propositions={
          hasSearchText && searchSuggestions.length ? (
            <ResourcesSearchPropositions
              variant="compliance"
              compliance_documents={searchSuggestions}
              searchQuery={trimmedSearchText}
              onComplianceClick={(doc) => {
                if (!doc.file_path) return;

                const pathParts = doc.file_path.split('/');
                if (pathParts.length <= 1) return;

                const normalizedFilePathParts = [...pathParts];
                const folderPathParts = [...pathParts];
                folderPathParts.pop(); // remove filename

                if (
                  selectedMarket &&
                  folderPathParts.length >= 3 &&
                  folderPathParts[2] !== selectedMarket.market_name
                ) {
                  folderPathParts[2] = selectedMarket.market_name;
                  normalizedFilePathParts[2] =
                    selectedMarket.market_name;
                }

                setPropositionDoc({
                  ...doc,
                  file_path: normalizedFilePathParts.join('/'),
                });
                setGridMode('proposition');
                setCommittedSearchText('');
                setPath(folderPathParts);
                setFocusedDocumentId(doc.id);
                addMany('compliance_documents', [doc.id]);
              }}
            />
          ) : null
        }
      />
      <ExplorerWrapper>
        <FileExplorer
          folderStructure={
            gridMode === 'search'
              ? EMPTY_FOLDER_STRUCTURE
              : gridMode === 'proposition'
                ? propositionFolderStructure
                : folderStructure
          }
          files={explorerFiles}
          filesFlatList={
            gridMode === 'explorer'
              ? (filesFlatData as ComplianceDocument[] | undefined)
              : undefined
          }
          loading={
            gridMode === 'search'
              ? isSearchResultsLoading || isSearchResultsFetching
              : gridMode === 'explorer'
                ? loading
                : false
          }
          selected={selected}
          onSelect={handleSelect}
          showDownload={false}
          showSelectedLabel={false}
          path={path}
          onPathChange={handlePathChange}
          hiddenRoot={COMPLIANCE_HIDDEN_ROOT}
          currentView={currentView}
          showHeader={false}
          downloadLoading={downloadLoading}
          loadingNextPage={
            gridMode === 'search'
              ? isSearchResultsFetchingNextPage
              : gridMode === 'explorer'
                ? loadingNextPage
                : false
          }
          hasActiveSearchOrFilter={hasActiveSearchOrFilter}
          onAllFilesClick={clearSearchState}
          exactMatchQuery={
            gridMode === 'search'
              ? trimmedCommittedSearchText
              : undefined
          }
        />
      </ExplorerWrapper>
      <BasketBar
        variant="sticky"
        category="compliance_documents"
        loadedIds={loadedIds}
        totalCount={loadedTotal}
        onShareClick={() => setShareOpen(true)}
      />
      <ShareBasketModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
      />
    </Root>
  );
};

const Root = styled('div')(({ theme }) => ({
  width: '100%',
  padding: `0 ${theme.spacing(4)}`,
  // Fill the scrollport and lay the page out as a column, so the basket bar
  // below can be pushed to the bottom edge.
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
}));

// The file list used to be separated from the header by the explorer's own
// header row, which this page no longer renders.
const ExplorerWrapper = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(3),
  // Takes the slack on a short page so the basket bar lands at the bottom;
  flex: '1 0 auto',
}));

export default ComplianceDocumentsResourcesPage;
